import { useEffect, useState } from 'react';
import { Formik, Form, Field, ErrorMessage, FieldArray } from 'formik';
import Breadcrumb from '../../components/Breadcrumb';
import * as Yup from 'yup';
import axios from 'axios';
import { BASEURL, API_SECRET, API_KEY } from '../Constants';
import { getAccessToken } from '../../services/http-common';
import AddIcon from '../../images/icon/add-icon.png';
import { Link, useParams } from 'react-router-dom';
import dayjs from 'dayjs';

export interface AddressInterface {
  locality: string;
  streetName: string;
  city: string;
  pin: string;
  state: string;
  country: string;
}

interface CustomerInterface {
  customerId?: string | null;
  firstName: string;
  lastName: string;
  email: string;
  contactNumber: string;
  companyName: string;
  password: string;
  addresses: AddressInterface[];
}

interface OrderInterface {
  orderId?: any | null;
  amount: string;
  status: string;
  paymentMode: string;
  date: string;
  orderTotal: string;
  couponCode?: string;
  discountAmount?: string;
  couponDiscount?: string;
}
type Params = {
  customerId: string;
};
const CustomerDetails = () => {
  const { customerId } = useParams<Params>();
  const [initialValues, setInitialValues] = useState<CustomerInterface>({
    customerId: customerId,
    firstName: '',
    lastName: '',
    email: '',
    contactNumber: '',
    companyName: '',
    password: '',
    addresses: [
      {
        locality: '',
        streetName: '',
        city: '',
        pin: '',
        state: '',
        country: '',
      },
    ],
  });

  const [customerOrders, setCustomerOrders] = useState<OrderInterface[]>([]);
  const [totalAmount, setTotalAmount] = useState<number>(0);
  const [isLoadingOrders, setIsLoadingOrders] = useState<boolean>(true);
  const [collapsedAddresses, setCollapsedAddresses] = useState<{[key: number]: boolean}>({});

  const validationSchema = Yup.object({
    firstName: Yup.string().required('First Name is required'),
    lastName: Yup.string().required('Last name is required'),
    email: Yup.string().required('Email is required'),
    contactNumber: Yup.string().required('Contact number is required'),
    password: Yup.string().required('Password is required'),
    addresses: Yup.array().of(
      Yup.object().shape({
        locality: Yup.string().required('Locality is required'),
        streetName: Yup.string().required('Street name is required'),
        city: Yup.string().required('City is required'),
        pin: Yup.string().required('Pin is required'),
        state: Yup.string().required('State is required'),
        country: Yup.string().required('Country is required'),
      }),
    ),
  });

  const accessToken = getAccessToken();

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (customerId != undefined) {
          // Fetch customer details first
          const response = await axios.get(
            `${BASEURL}customers/${customerId}`,
            {
              headers: {
                Authorization: `Bearer ${accessToken}`,
                'Content-Type': 'application/json',
                'X-API-SECRET': `${API_SECRET}`,
                'X-API-KEY': `${API_KEY}`,
              },
            },
          );
          const responseData = response.data;
          setInitialValues(responseData);
          
          // Wait a bit for state to update, then fetch orders
          setTimeout(() => {
            fetchCustomerOrdersWithData(responseData);
          }, 100);
        } 
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, []);

  const fetchCustomerOrdersWithData = async (customerData: CustomerInterface) => {
    try {
      setIsLoadingOrders(true);
      console.log('Fetching orders for customer:', customerData);
      
      const response = await axios.get(`${BASEURL}orders/`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
          'X-API-SECRET': `${API_SECRET}`,
          'X-API-KEY': `${API_KEY}`,
        },
      });
      
      const allOrders = response.data;
      console.log('All orders received:', allOrders.length);
      console.log('Sample order structure:', allOrders[0]);
      
      // Filter orders by multiple criteria using customer data
      const filteredOrders = allOrders.filter((order: any) => {
        // Try customer ID matching
        const idMatches = order.customerId === customerId || 
                         order.customer_id === customerId ||
                         order.customerID === customerId ||
                         (order.customer && order.customer.customerId === customerId) ||
                         (order.customer && order.customer.id === customerId);
        
        // Try email matching
        const emailMatches = customerData.email && (
          order.email === customerData.email ||
          order.customerEmail === customerData.email ||
          (order.customer && order.customer.email === customerData.email)
        );
        
        // Try name matching
        const customerFullName = `${customerData.firstName} ${customerData.lastName}`.trim();
        const nameMatches = customerFullName && (
          order.customerName === customerFullName ||
          order.customer_name === customerFullName ||
          (order.customer && order.customer.name === customerFullName) ||
          (order.customer && `${order.customer.firstName} ${order.customer.lastName}` === customerFullName)
        );
        
        // Try contact number matching
        const contactMatches = customerData.contactNumber && (
          order.contactNumber === customerData.contactNumber ||
          order.customerPhone === customerData.contactNumber ||
          order.phone === customerData.contactNumber ||
          (order.customer && order.customer.contactNumber === customerData.contactNumber)
        );
        
        const matches = idMatches || emailMatches || nameMatches || contactMatches;
        
        if (matches) {
          console.log('Found matching order:', order.orderId, 'matched by:', {
            id: idMatches,
            email: emailMatches,
            name: nameMatches,
            contact: contactMatches
          });
        }
        return matches;
      });
      
      console.log('Filtered orders for customer:', filteredOrders.length);
      setCustomerOrders(filteredOrders);
      
      // Calculate total amount from completed orders
      const completedStatuses = ['PAYMENT_DONE', 'ORDER_COMPLETED', 'ORDER_DELIVERED', 'ORDER_SHIPPED'];
      const total = filteredOrders
        .filter((order: OrderInterface) => completedStatuses.includes(order.status))
        .reduce((sum: number, order: OrderInterface) => {
          return sum + (parseFloat(order.orderTotal || order.amount) || 0);
        }, 0);
      
      console.log('Total amount calculated:', total);
      setTotalAmount(total);
      setIsLoadingOrders(false);
    } catch (error) {
      console.error('Error fetching customer orders:', error);
      setIsLoadingOrders(false);
    }
  };

  const fetchCustomerOrders = async () => {
    try {
      setIsLoadingOrders(true);
      console.log('Fetching orders for customer ID:', customerId);
      console.log('Customer email:', initialValues.email);
      console.log('Customer name:', `${initialValues.firstName} ${initialValues.lastName}`);
      
      const response = await axios.get(`${BASEURL}orders/`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
          'X-API-SECRET': `${API_SECRET}`,
          'X-API-KEY': `${API_KEY}`,
        },
      });
      
      const allOrders = response.data;
      console.log('All orders received:', allOrders.length);
      console.log('Sample order structure:', allOrders[0]);
      
      // Filter orders by multiple criteria
      const filteredOrders = allOrders.filter((order: any) => {
        // Try customer ID matching
        const idMatches = order.customerId === customerId || 
                         order.customer_id === customerId ||
                         order.customerID === customerId ||
                         (order.customer && order.customer.customerId === customerId) ||
                         (order.customer && order.customer.id === customerId);
        
        // Try email matching
        const emailMatches = initialValues.email && (
          order.email === initialValues.email ||
          order.customerEmail === initialValues.email ||
          (order.customer && order.customer.email === initialValues.email)
        );
        
        // Try name matching
        const customerFullName = `${initialValues.firstName} ${initialValues.lastName}`.trim();
        const nameMatches = customerFullName && (
          order.customerName === customerFullName ||
          order.customer_name === customerFullName ||
          (order.customer && order.customer.name === customerFullName) ||
          (order.customer && `${order.customer.firstName} ${order.customer.lastName}` === customerFullName)
        );
        
        // Try contact number matching
        const contactMatches = initialValues.contactNumber && (
          order.contactNumber === initialValues.contactNumber ||
          order.customerPhone === initialValues.contactNumber ||
          (order.customer && order.customer.contactNumber === initialValues.contactNumber)
        );
        
        const matches = idMatches || emailMatches || nameMatches || contactMatches;
        
        if (matches) {
          console.log('Found matching order:', order.orderId, 'matched by:', {
            id: idMatches,
            email: emailMatches,
            name: nameMatches,
            contact: contactMatches
          });
        }
        return matches;
      });
      
      console.log('Filtered orders for customer:', filteredOrders.length);
      setCustomerOrders(filteredOrders);
      
      // Calculate total amount from completed orders
      const completedStatuses = ['PAYMENT_DONE', 'ORDER_COMPLETED', 'ORDER_DELIVERED', 'ORDER_SHIPPED'];
      const total = filteredOrders
        .filter((order: OrderInterface) => completedStatuses.includes(order.status))
        .reduce((sum: number, order: OrderInterface) => {
          return sum + (parseFloat(order.orderTotal || order.amount) || 0);
        }, 0);
      
      console.log('Total amount calculated:', total);
      setTotalAmount(total);
      setIsLoadingOrders(false);
    } catch (error) {
      console.error('Error fetching customer orders:', error);
      setIsLoadingOrders(false);
    }
  };

  const formatAmount = (value: number | string) => {
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(numValue || 0);
  };

  const formatDate = (dateString: string) => {
    return dayjs(dateString).format('DD MMM YYYY');
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PAYMENT_DONE':
        return 'bg-success text-white';
      case 'ORDER_COMPLETED':
      case 'ORDER_DELIVERED':
        return 'bg-primary text-white';
      case 'ORDER_SHIPPED':
        return 'bg-meta-5 text-white';
      case 'ORDER_PENDING':
        return 'bg-meta-8 text-white';
      case 'PAYMENT_FAILED':
      case 'PAYMENT_CANCELLED':
      case 'ORDER_CANCELLED':
        return 'bg-danger text-white';
      default:
        return 'bg-meta-7 text-white';
    }
  };

  const toggleAddressCollapse = (index: number) => {
    setCollapsedAddresses(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const onSubmit = async (values: any, { setSubmitting }: any) => {
    try {
      const response = await axios.post(`${BASEURL}customers/`, values, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
          'X-API-SECRET': `${API_SECRET}`,
          'X-API-KEY': `${API_KEY}`,
        },
      });
      window.location.href = '/customers';
    } catch (error) {
      console.error('Error uploading file:', error);
    }

    setSubmitting(false);
  };

  return (
    <div>
      <div>
        <Breadcrumb pageName="Customer details" />
      </div>
      <Formik
        initialValues={initialValues}
        enableReinitialize={true}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
      >
        {({ values }) => (
          <Form>
            <div className="grid grid-cols-1 gap-9 sm:grid-cols-2">
              <div className="flex flex-col gap-9">
                {/* <!-- Input Fields --> */}
                <div className="rounded-sm border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark">
                  <div className=" flex space-x-1 border-b border-stroke py-3 px-4 dark:border-strokedark">
                    <h3 className="font-medium text-black dark:text-white">
                      Customer Details
                    </h3>
                    <span className=" -mt-1 ">
                      <Link
                        className="hover:text-primary"
                        to={`/customers/update/${customerId}`}
                      >
                        <svg
                          className="feather feather-edit text-primary "
                          fill="none"
                          height="30"
                          stroke="currentColor"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          viewBox="0 0 24 24"
                          width="20"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                        </svg>
                      </Link>
                    </span>
                  </div>
                  <div className="flex flex-col gap-4 p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="mb-1 block text-black dark:text-white">
                          First name
                        </label>
                        <Field
                          type="text"
                          name="firstName"
                          disabled
                          placeholder="First name"
                          className="w-full rounded-lg border-[1.5px] border-stroke bg-transparent py-1 px-4 font-medium outline-none transition focus:border-primary active:border-primary disabled:cursor-default disabled:bg-whiter dark:border-form-strokedark dark:bg-form-input dark:focus:border-primary"
                        />
                      </div>

                      <div>
                        <label className="mb-1 block text-black dark:text-white">
                          Last name
                        </label>
                        <Field
                          type="text"
                          name="lastName"
                          disabled
                          placeholder="Last Name"
                          className="w-full rounded-lg border-[1.5px] border-primary bg-transparent py-1 px-4 font-medium outline-none transition focus:border-primary active:border-primary disabled:cursor-default disabled:bg-whiter dark:bg-form-input"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="mb-1 block text-black dark:text-white">
                          Email
                        </label>
                        <Field
                          type="text"
                          name="email"
                          disabled
                          placeholder="Email"
                          className="w-full rounded-lg border-[1.5px] border-stroke bg-transparent py-1 px-4 font-medium outline-none transition focus:border-primary active:border-primary disabled:cursor-default disabled:bg-whiter dark:border-form-strokedark dark:bg-form-input dark:focus:border-primary dark:disabled:bg-black"
                        />
                      </div>
                      <div>
                        <label className="mb-1 block  text-black dark:text-white">
                          Contact Number
                        </label>
                        <Field
                          type="text"
                          name="contactNumber"
                          disabled
                          placeholder="Contact number"
                          className="w-full rounded-lg border-[1.5px] border-stroke bg-transparent py-1 px-4 font-medium outline-none transition focus:border-primary active:border-primary disabled:cursor-default disabled:bg-whiter dark:border-form-strokedark dark:bg-form-input dark:focus:border-primary dark:disabled:bg-black"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="mb-1 block  text-black dark:text-white">
                        Company Name
                      </label>
                      <Field
                        type="text"
                        name="companyName"
                        disabled
                        placeholder=" Company Name"
                        className="w-full rounded-lg border-[1.5px] border-stroke bg-transparent py-1 px-4 font-medium outline-none transition focus:border-primary active:border-primary disabled:cursor-default disabled:bg-whiter dark:border-form-strokedark dark:bg-form-input dark:focus:border-primary dark:disabled:bg-black"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <FieldArray name="addresses">
                {({}) => (
                  <div>
                    {' '}
                    {values.addresses.map((item: any, index: any) => (
                      <div
                        key={index}
                        className="relative flex flex-col gap-9 mb-3"
                      >
                        {/* <!-- Textarea Fields --> */}
                        <div className="rounded-sm border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark">
                          <div className="flex justify-between items-center border-b border-stroke py-3 px-4 dark:border-strokedark cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800" 
                               onClick={() => toggleAddressCollapse(index)}>
                            <h3 className="font-medium text-black dark:text-white">
                              Address {index + 1}
                            </h3>
                            <button
                              type="button"
                              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                            >
                              <svg
                                className={`w-5 h-5 transform transition-transform duration-200 ${
                                  collapsedAddresses[index] ? 'rotate-180' : ''
                                }`}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M19 9l-7 7-7-7"
                                />
                              </svg>
                            </button>
                          </div>
                          {!collapsedAddresses[index] && (
                            <div className="flex flex-col gap-4 p-4">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <label className="mb-1 block text-black dark:text-white">
                                  Locality
                                </label>
                                <Field
                                  type="text"
                                  name={`addresses[${index}].locality`}
                                  disabled
                                  placeholder="Locality"
                                  className="w-full rounded-lg border-[1.5px] border-stroke bg-transparent py-1 px-4 font-medium outline-none transition focus:border-primary active:border-primary disabled:cursor-default disabled:bg-whiter dark:border-form-strokedark dark:bg-form-input dark:focus:border-primary"
                                />
                              </div>

                              <div>
                                <label className="mb-1 block text-black dark:text-white">
                                  Street name
                                </label>
                                <Field
                                  type="text"
                                  name={`addresses[${index}].streetName`}
                                  placeholder=" Street name"
                                  disabled
                                  className="w-full rounded-lg border-[1.5px] border-primary bg-transparent py-1 px-4 font-medium outline-none transition focus:border-primary active:border-primary disabled:cursor-default disabled:bg-whiter dark:bg-form-input"
                                />
                              </div>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                              <div>
                                <label className="mb-1 block text-black dark:text-white">
                                  City
                                </label>
                                <Field
                                  type="text"
                                  name={`addresses[${index}].city`}
                                  disabled
                                  placeholder="City"
                                  className="w-full rounded-lg border-[1.5px] border-stroke bg-transparent py-1 px-4 font-medium outline-none transition focus:border-primary active:border-primary disabled:cursor-default disabled:bg-whiter dark:border-form-strokedark dark:bg-form-input dark:focus:border-primary dark:disabled:bg-black"
                                />
                              </div>
                              <div>
                                <label className="mb-1 block  text-black dark:text-white">
                                  Pin Code
                                </label>
                                <Field
                                  type="text"
                                  name={`addresses[${index}].pin`}
                                  disabled
                                  placeholder=" Pin Code"
                                  className="w-full rounded-lg border-[1.5px] border-stroke bg-transparent py-1 px-4 font-medium outline-none transition focus:border-primary active:border-primary disabled:cursor-default disabled:bg-whiter dark:border-form-strokedark dark:bg-form-input dark:focus:border-primary dark:disabled:bg-black"
                                />
                              </div>
                              <div>
                                <label className="mb-1 block  text-black dark:text-white">
                                  State
                                </label>
                                <Field
                                  type="text"
                                  name={`addresses[${index}].state`}
                                  disabled
                                  placeholder="State"
                                  className="w-full rounded-lg border-[1.5px] border-stroke bg-transparent py-1 px-4 font-medium outline-none transition focus:border-primary active:border-primary disabled:cursor-default disabled:bg-whiter dark:border-form-strokedark dark:bg-form-input dark:focus:border-primary dark:disabled:bg-black"
                                />
                              </div>
                            </div>
                            <div>
                              <label className="mb-1 block  text-black dark:text-white">
                                Country
                              </label>
                              <Field
                                type="text"
                                name={`addresses[${index}].country`}
                                disabled
                                placeholder="Country"
                                className="w-full rounded-lg border-[1.5px] border-stroke bg-transparent py-1 px-4 font-medium outline-none transition focus:border-primary active:border-primary disabled:cursor-default disabled:bg-whiter dark:border-form-strokedark dark:bg-form-input dark:focus:border-primary dark:disabled:bg-black"
                              />
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </FieldArray>
            </div>
          </Form>
        )}
      </Formik>

      {/* Customer Orders Section */}
      <div className="mt-6">
        <div className="rounded-sm border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark">
          <div className="border-b border-stroke py-2 px-4 dark:border-strokedark">
            <div className="flex justify-between items-center">
              <h3 className="font-medium text-black dark:text-white">
                Customer Orders
              </h3>
              <div className="text-right">
                <p className="text-xl font-bold text-primary">
                  {formatAmount(totalAmount)}
                </p>
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  Total Amount Received
                </p>
              </div>
            </div>
          </div>
          
          <div className="p-4">
            {isLoadingOrders ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                <span className="ml-2 text-gray-600 dark:text-gray-400">Loading orders...</span>
              </div>
            ) : customerOrders.length === 0 ? (
              <div className="text-center py-6">
                <p className="text-gray-600 dark:text-gray-400">No orders found for this customer.</p>
                <p className="text-xs text-gray-500 dark:text-gray-500 mt-2">
                  Customer ID: {customerId}
                </p>
                <button 
                  onClick={() => {
                    console.log('Debug: Retrying order fetch');
                    fetchCustomerOrdersWithData(initialValues);
                  }}
                  className="mt-2 text-primary hover:underline text-sm"
                >
                  Retry Loading Orders
                </button>
              </div>
            ) : (
              <>
                <div className="mb-3">
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    Showing {customerOrders.length} order{customerOrders.length !== 1 ? 's' : ''}
                  </p>
                </div>
                
                {/* Desktop view */}
                <div className="hidden md:block">
                  <div className="grid grid-cols-5 gap-3 border-b border-stroke py-2 dark:border-strokedark">
                    <div className="font-medium text-black dark:text-white text-sm">Order ID</div>
                    <div className="font-medium text-black dark:text-white text-sm">Date</div>
                    <div className="font-medium text-black dark:text-white text-sm">Status</div>
                    <div className="font-medium text-black dark:text-white text-sm">Amount</div>
                    <div className="font-medium text-black dark:text-white text-sm">Action</div>
                  </div>
                  
                  {customerOrders.map((order, index) => (
                    <div key={index} className="grid grid-cols-5 gap-3 border-b border-stroke py-2 dark:border-strokedark">
                      <div className="text-black dark:text-white text-sm">
                        <Link 
                          to={`/orders/details/${order.orderId}`}
                          className="text-primary hover:underline"
                        >
                          {order.orderId}
                        </Link>
                      </div>
                      <div className="text-black dark:text-white text-sm">
                        {formatDate(order.date)}
                      </div>
                      <div>
                        <span className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${getStatusColor(order.status)}`}>
                          {order.status === 'PAYMENT_DONE' && order.paymentMode === 'cod' ? 'PARTIAL_PAYMENT' : 
                           order.status === 'PAYMENT_DONE' ? 'ORDER_CONFIRMED' : order.status}
                        </span>
                      </div>
                      <div className="text-black dark:text-white font-medium text-sm">
                        {formatAmount(order.orderTotal || order.amount)}
                      </div>
                      <div>
                        <Link
                          to={`/orders/details/${order.orderId}`}
                          className="text-primary hover:text-primary-dark"
                        >
                          <svg
                            className="fill-current"
                            width="16"
                            height="16"
                            viewBox="0 0 18 18"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M8.99981 14.8219C3.43106 14.8219 0.674805 9.50624 0.562305 9.28124C0.47793 9.11249 0.47793 8.88749 0.562305 8.71874C0.674805 8.49374 3.43106 3.20624 8.99981 3.20624C14.5686 3.20624 17.3248 8.49374 17.4373 8.71874C17.5217 8.88749 17.5217 9.11249 17.4373 9.28124C17.3248 9.50624 14.5686 14.8219 8.99981 14.8219ZM1.85605 8.99999C2.4748 10.0406 4.89356 13.5562 8.99981 13.5562C13.1061 13.5562 15.5248 10.0406 16.1436 8.99999C15.5248 7.95936 13.1061 4.44374 8.99981 4.44374C4.89356 4.44374 2.4748 7.95936 1.85605 8.99999Z"
                              fill=""
                            />
                            <path
                              d="M9 11.3906C7.67812 11.3906 6.60938 10.3219 6.60938 9C6.60938 7.67813 7.67812 6.60938 9 6.60938C10.3219 6.60938 11.3906 7.67813 11.3906 9C11.3906 10.3219 10.3219 11.3906 9 11.3906ZM9 7.875C8.38125 7.875 7.875 8.38125 7.875 9C7.875 9.61875 8.38125 10.125 9 10.125C9.61875 10.125 10.125 9.61875 10.125 9C10.125 8.38125 9.61875 7.875 9 7.875Z"
                              fill=""
                            />
                          </svg>
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Mobile view */}
                <div className="md:hidden space-y-3">
                  {customerOrders.map((order, index) => (
                    <div key={index} className="border border-stroke rounded-lg p-3 dark:border-strokedark">
                      <div className="flex justify-between items-start mb-2">
                        <Link 
                          to={`/orders/details/${order.orderId}`}
                          className="text-primary hover:underline font-medium text-sm"
                        >
                          {order.orderId}
                        </Link>
                        <span className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${getStatusColor(order.status)}`}>
                          {order.status === 'PAYMENT_DONE' && order.paymentMode === 'cod' ? 'PARTIAL_PAYMENT' : 
                           order.status === 'PAYMENT_DONE' ? 'ORDER_CONFIRMED' : order.status}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div>
                          <span className="text-gray-600 dark:text-gray-400">Date:</span>
                          <span className="ml-1 text-black dark:text-white">{formatDate(order.date)}</span>
                        </div>
                        <div>
                          <span className="text-gray-600 dark:text-gray-400">Amount:</span>
                          <span className="ml-1 text-black dark:text-white font-medium">{formatAmount(order.orderTotal || order.amount)}</span>
                        </div>
                        <div className="col-span-2 flex justify-end mt-1">
                          <Link
                            to={`/orders/details/${order.orderId}`}
                            className="text-primary hover:text-primary-dark text-sm"
                          >
                            View Details
                          </Link>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerDetails;
